/*
キーボード図の並びと、指の担当。

配列だけを持ち、描画は KeyMap.tsx が受け持つ。キー 1 つを KeyDef、
段をその配列、盤面全体を段の配列として持つ。

指の色は白い面に arena の変数を 10〜18% だけ混ぜた淡い色で作る。
盤面は淡いまま、次に打つ 1 キーだけを濃い緑で塗る。以前は指ごとに
濃さを変えて 8 段のグラデーションにしていたが、盤面が濃い緑の塊に
なって打つ語より目立ったので、この関係を反転させた
*/

export type Finger =
  | "left-pinky"
  | "left-ring"
  | "left-middle"
  | "left-index"
  | "right-index"
  | "right-middle"
  | "right-ring"
  | "right-pinky"
  | "thumb";

export type KeyDef = {
  /** DOM の data-key と照合するための名。英字は小文字で持つ */
  id: string;
  /** 面上に出す字 */
  label: string;
  /** Shift の面に出る字。数字段の記号のように 2 段で持つキーだけ */
  shiftLabel?: string;
  /** 通常の 1 キーを 1 とした幅。段の中で flexGrow にそのまま渡す */
  width: number;
  finger: Finger;
  /** そのまま押して出る字 */
  chars?: readonly string[];
  /** Shift と一緒に押して出る字 */
  shiftChars?: readonly string[];
};

const letter = (id: string, finger: Finger): KeyDef => ({
  id,
  label: id.toUpperCase(),
  width: 1,
  finger,
  chars: [id],
  shiftChars: [id.toUpperCase()],
});

/*
標準的なホームポジションの担当。左人差し指は T G B まで、右人差し指は
Y H N まで横に 2 列面倒を見る。修飾キーは小指の仕事なので finger は
小指にするが、面には色を敷かない (KeyMap 側で chars の無いキーは
地の色にする)。幅の広い修飾キーまで塗ると盤面の 4 割が小指の色になり、
字のキーの色分けが読めなくなるため
*/
export const KEY_ROWS: readonly (readonly KeyDef[])[] = [
  [
    {
      id: "`",
      label: "`",
      shiftLabel: "~",
      width: 1,
      finger: "left-pinky",
      chars: ["`"],
      shiftChars: ["~"],
    },
    { id: "1", label: "1", shiftLabel: "!", width: 1, finger: "left-pinky", chars: ["1"], shiftChars: ["!"] },
    { id: "2", label: "2", shiftLabel: "@", width: 1, finger: "left-ring", chars: ["2"], shiftChars: ["@"] },
    { id: "3", label: "3", shiftLabel: "#", width: 1, finger: "left-middle", chars: ["3"], shiftChars: ["#"] },
    { id: "4", label: "4", shiftLabel: "$", width: 1, finger: "left-index", chars: ["4"], shiftChars: ["$"] },
    { id: "5", label: "5", shiftLabel: "%", width: 1, finger: "left-index", chars: ["5"], shiftChars: ["%"] },
    { id: "6", label: "6", shiftLabel: "^", width: 1, finger: "right-index", chars: ["6"], shiftChars: ["^"] },
    { id: "7", label: "7", shiftLabel: "&", width: 1, finger: "right-index", chars: ["7"], shiftChars: ["&"] },
    { id: "8", label: "8", shiftLabel: "*", width: 1, finger: "right-middle", chars: ["8"], shiftChars: ["*"] },
    { id: "9", label: "9", shiftLabel: "(", width: 1, finger: "right-ring", chars: ["9"], shiftChars: ["("] },
    { id: "0", label: "0", shiftLabel: ")", width: 1, finger: "right-pinky", chars: ["0"], shiftChars: [")"] },
    { id: "-", label: "-", shiftLabel: "_", width: 1, finger: "right-pinky", chars: ["-"], shiftChars: ["_"] },
    { id: "=", label: "=", shiftLabel: "+", width: 1, finger: "right-pinky", chars: ["="], shiftChars: ["+"] },
    { id: "backspace", label: "⌫", width: 2, finger: "right-pinky" },
  ],
  [
    { id: "tab", label: "Tab", width: 1.5, finger: "left-pinky" },
    letter("q", "left-pinky"),
    letter("w", "left-ring"),
    letter("e", "left-middle"),
    letter("r", "left-index"),
    letter("t", "left-index"),
    letter("y", "right-index"),
    letter("u", "right-index"),
    letter("i", "right-middle"),
    letter("o", "right-ring"),
    letter("p", "right-pinky"),
    { id: "[", label: "[", shiftLabel: "{", width: 1, finger: "right-pinky", chars: ["["], shiftChars: ["{"] },
    { id: "]", label: "]", shiftLabel: "}", width: 1, finger: "right-pinky", chars: ["]"], shiftChars: ["}"] },
    { id: "enter", label: "↵", width: 1.5, finger: "right-pinky" },
  ],
  [
    { id: "caps", label: "Caps", width: 1.75, finger: "left-pinky" },
    letter("a", "left-pinky"),
    letter("s", "left-ring"),
    letter("d", "left-middle"),
    letter("f", "left-index"),
    letter("g", "left-index"),
    letter("h", "right-index"),
    letter("j", "right-index"),
    letter("k", "right-middle"),
    letter("l", "right-ring"),
    { id: ";", label: ";", shiftLabel: ":", width: 1, finger: "right-pinky", chars: [";"], shiftChars: [":"] },
    { id: "'", label: "'", shiftLabel: "\"", width: 1, finger: "right-pinky", chars: ["'"], shiftChars: ["\""] },
    { id: "\\", label: "\\", shiftLabel: "|", width: 1.25, finger: "right-pinky", chars: ["\\"], shiftChars: ["|"] },
  ],
  [
    { id: "shift-left", label: "Shift", width: 2.25, finger: "left-pinky" },
    letter("z", "left-pinky"),
    letter("x", "left-ring"),
    letter("c", "left-middle"),
    letter("v", "left-index"),
    letter("b", "left-index"),
    letter("n", "right-index"),
    letter("m", "right-index"),
    { id: ",", label: ",", shiftLabel: "<", width: 1, finger: "right-middle", chars: [","], shiftChars: ["<"] },
    { id: ".", label: ".", shiftLabel: ">", width: 1, finger: "right-ring", chars: ["."], shiftChars: [">"] },
    { id: "/", label: "/", shiftLabel: "?", width: 1, finger: "right-pinky", chars: ["/"], shiftChars: ["?"] },
    { id: "shift-right", label: "Shift", width: 2.75, finger: "right-pinky" },
  ],
  [
    { id: "ctrl-left", label: "Ctrl", width: 1.5, finger: "left-pinky" },
    { id: "alt-left", label: "Alt", width: 1.25, finger: "thumb" },
    { id: "space", label: "", width: 6.5, finger: "thumb", chars: [" "] },
    { id: "alt-right", label: "Alt", width: 1.25, finger: "thumb" },
    { id: "ctrl-right", label: "Ctrl", width: 1.5, finger: "right-pinky" },
  ],
];

/*
白い面に色を pct% だけ混ぜる。原色をそのまま塗ると、盤面が打つ語より
先に目に入る
*/
const tint = (color: string, pct: number) =>
  `color-mix(in srgb, ${color} ${pct}%, var(--arena-cream))`;

/*
キーの面の色。字はどれも濃い緑 (--arena-navy) で読ませる。

左右の同じ指は同じ色にする。「小指は朱、薬指は黄、人差し指は緑」と
覚えれば両手に効くため。中指は白のまま残し、色の面が 3 本続かない
ようにして盤面を軽く保つ。

人差し指だけは左右が隣り合うので、同じ緑でも濃さを 10% と 18% に
分けて手の境目を残す。黄は明るくて 12% では白と見分けにくいので
18% まで上げ、朱は 12% で十分に色として立つ
*/
export const FINGER_TINT: Record<Finger, string> = {
  "left-pinky": tint("var(--arena-coral)", 12),
  "left-ring": tint("var(--arena-yellow)", 18),
  "left-middle": "var(--arena-cream)",
  "left-index": tint("var(--arena-mint)", 10),
  "right-index": tint("var(--arena-mint)", 18),
  "right-middle": "var(--arena-cream)",
  "right-ring": tint("var(--arena-yellow)", 18),
  "right-pinky": tint("var(--arena-coral)", 12),
  /*
  親指の仕事はスペースと Alt だけ。指の色を足すと 4 色目で画面が
  騒がしくなるので、サイトの地の色 (生成り) で沈ませる
  */
  thumb: "var(--arena-field)",
};

export type ResolvedKey = {
  keyId: string;
  /** 一緒に押すべき Shift の側。要らなければ null */
  shift: "left" | "right" | null;
};

/*
文字からキーへの逆引き表。モジュールを読んだ時点で 1 度だけ作る。
打鍵ごとに全キーを舐め直すと、1 打につき 60 キーぶんの走査になる
*/
const LOOKUP = new Map<string, { keyId: string; shifted: boolean }>();
const FINGER_BY_KEY = new Map<string, Finger>();
for (const row of KEY_ROWS) {
  for (const key of row) {
    FINGER_BY_KEY.set(key.id, key.finger);
    for (const char of key.chars ?? []) {
      LOOKUP.set(char, { keyId: key.id, shifted: false });
    }
    for (const char of key.shiftChars ?? []) {
      LOOKUP.set(char, { keyId: key.id, shifted: true });
    }
  }
}

/**
 * 次に打つ 1 文字から、光らせるキーと押すべき Shift を引く。
 *
 * 表に無い字 (日本語など) は null を返す。お題の読みが素通しの
 * かなを含んでいても、図が落ちることはない。
 *
 * Shift は反対側の手で押す決まり。右手のキーを打つときは左の
 * Shift、という対応を指の担当から引く
 */
export function resolveKey(char: string | undefined): ResolvedKey | null {
  if (!char) return null;
  const hit = LOOKUP.get(char);
  if (!hit) return null;
  if (!hit.shifted) return { keyId: hit.keyId, shift: null };
  const finger = FINGER_BY_KEY.get(hit.keyId);
  const onLeft = finger?.startsWith("left") ?? false;
  return { keyId: hit.keyId, shift: onLeft ? "right" : "left" };
}
